import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, RefreshCw, BadgeCheck, AlertCircle,
  ChevronRight, Building2, CalendarCheck, CircleDot,
  IndianRupee, ChevronLeft, Upload,
} from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  ONBOARDED_URL, USERS_SIMPLE_URL,
} from "../../constants/api";
import AddOnboardedModal from "./AddOnboardedModal";
import BulkUploadOnboardedModal from "./BulkUploadOnboardedModal";

// ── helpers ──────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  "In Progress": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "Go Live":     "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "Completed":   "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  "Suspended":   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const AMC_COLORS = {
  "Paid":    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "Pending": "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  "Overdue": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  "Waived":  "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const fmtCurrency = (val) =>
  val ? `₹${parseFloat(val).toLocaleString("en-IN")}` : "—";

const daysLeft = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const AMCBadge = ({ amc }) => {
  if (!amc?.amc_end_date) return <span className="text-gray-400 dark:text-gray-500 text-xs">No AMC</span>;
  const left = daysLeft(amc.amc_end_date);
  const label =
    left < 0   ? `Overdue ${Math.abs(left)}d` :
    left <= 30 ? `Expiring in ${left}d`        :
    amc.payment_status;
  const color =
    left < 0   ? AMC_COLORS["Overdue"]  :
    left <= 30 ? AMC_COLORS["Pending"]  :
    AMC_COLORS[amc.payment_status] || AMC_COLORS["Pending"];
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
  );
};

// ── Component ────────────────────────────────────────────────────────────────
export default function OnboardedList() {
  const { fetch } = useApiMethods();
  const navigate   = useNavigate();

  const [data,        setData]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [users,       setUsers]       = useState([]);
  const [page,        setPage]        = useState(1);
  const pageSize = 20;

  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [amcFilter,    setAmcFilter]    = useState("");
  const [ownerFilter,  setOwnerFilter]  = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const [summary, setSummary] = useState({ total: 0, live: 0, amc_overdue: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${ONBOARDED_URL}?page=${page}&page_size=${pageSize}`;
      if (search)       url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&onboarding_status=${encodeURIComponent(statusFilter)}`;
      if (amcFilter)    url += `&amc_status=${encodeURIComponent(amcFilter)}`;
      if (ownerFilter)  url += `&assigned_to=${ownerFilter}`;

      const res = await fetch(url);
      if (res?.status === "success") {
        setData(res.data || []);
        setTotal(res.total || 0);
        const items   = res.data || [];
        const live    = items.filter(c => c.onboarding_status === "Go Live" || c.onboarding_status === "Completed").length;
        const overdue = items.filter(c => c.amc?.amc_end_date && daysLeft(c.amc.amc_end_date) < 0).length;
        setSummary({ total: res.total || 0, live, amc_overdue: overdue });
      }
    } catch {
      ToastMsg("Failed to load onboarded clients", "error");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, amcFilter, ownerFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    fetch(USERS_SIMPLE_URL).then(res => {
      if (res?.status === "success") setUsers(res.data || []);
    });
  }, []);

  const handleAddSuccess = () => {
    setShowAdd(false);
    loadData();
    ToastMsg("Client onboarded successfully", "success");
  };

  const totalPages = Math.ceil(total / pageSize);

  const card = "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700";

  return (
    <ContainerCard>
      <div className="space-y-4 p-2">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-primary" />
              Onboarded Clients
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Manage your live clients, AMC renewals and implementation progress
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkUpload(true)}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Bulk Upload
            </Button>
            <Button onClick={() => setShowAdd(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Onboarded Client
            </Button>
          </div>
        </div>

        {/* ── Summary Cards ───────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-3">
          <div className={`${card} p-4 flex items-center gap-3`}>
            <div className="h-10 w-10 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.total}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Clients</p>
            </div>
          </div>
          <div className={`${card} p-4 flex items-center gap-3`}>
            <div className="h-10 w-10 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <BadgeCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.live}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Live / Completed</p>
            </div>
          </div>
          <div className={`${card} p-4 flex items-center gap-3`}>
            <div className="h-10 w-10 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.amc_overdue}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">AMC Overdue</p>
            </div>
          </div>
        </div>

        {/* ── Filters ─────────────────────────────────────────────── */}
        <div className={`${card} p-3`}>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search client name..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-8 h-9"
              />
            </div>
            <Select
              placeholder="Onboarding Status"
              allowClear
              style={{ minWidth: 170 }}
              value={statusFilter || undefined}
              onChange={v => { setStatusFilter(v || ""); setPage(1); }}
              options={[
                { value: "In Progress", label: "In Progress" },
                { value: "Go Live",     label: "Go Live"     },
                { value: "Completed",   label: "Completed"   },
                { value: "Suspended",   label: "Suspended"   },
              ]}
            />
            <Select
              placeholder="AMC Status"
              allowClear
              style={{ minWidth: 140 }}
              value={amcFilter || undefined}
              onChange={v => { setAmcFilter(v || ""); setPage(1); }}
              options={[
                { value: "Paid",    label: "Paid"    },
                { value: "Pending", label: "Pending" },
                { value: "Overdue", label: "Overdue" },
                { value: "Waived",  label: "Waived"  },
              ]}
            />
            <Select
              placeholder="Assigned To"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 160 }}
              value={ownerFilter || undefined}
              onChange={v => { setOwnerFilter(v || ""); setPage(1); }}
              options={users.map(u => ({
                value: String(u.id),
                label: `${u.login_fname || ""} ${u.login_lname || ""}`.trim(),
              }))}
            />
            <Button variant="outline" size="icon" onClick={loadData} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────── */}
        <div className={`${card} overflow-hidden`}>
          {loading ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              Loading...
            </div>
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400 dark:text-gray-500">
              <BadgeCheck className="h-10 w-10 opacity-30" />
              <p className="text-sm">No onboarded clients found</p>
              <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add First Client
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40">
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Client</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Go Live</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Contract Value</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">AMC</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Progress</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Assigned To</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {data.map(client => (
                      <tr
                        key={client.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
                        onClick={() => navigate(`/onboarded/${client.id}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{client.org_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {[client.city, client.state].filter(Boolean).join(", ") || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[client.onboarding_status] || "bg-gray-100 text-gray-600"}`}>
                            {client.onboarding_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-sm">
                          {client.go_live_date
                            ? new Date(client.go_live_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                            : "—"}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                          {fmtCurrency(client.contract_value)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-0.5">
                            <AMCBadge amc={client.amc} />
                            {client.amc?.amc_end_date && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Ends {new Date(client.amc.amc_end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden w-20">
                              <div
                                className="h-full bg-blue-500 rounded-full transition-all"
                                style={{ width: `${client.milestone_progress?.percent || 0}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 w-8">
                              {client.milestone_progress?.percent || 0}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                          {client.assigned_to_name || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="icon" className="h-7 w-7"
                      disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="icon" className="h-7 w-7"
                      disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {/* ── Add Modal ─────────────────────────────────────────────── */}
      {showAdd && (
        <AddOnboardedModal
          onClose={() => setShowAdd(false)}
          onSuccess={handleAddSuccess}
          users={users}
        />
      )}

      {/* ── Bulk Upload Modal ─────────────────────────────────────── */}
      {showBulkUpload && (
        <BulkUploadOnboardedModal
          onClose={() => setShowBulkUpload(false)}
          onSuccess={loadData}
        />
      )}
    </ContainerCard>
  );
}
